import { z } from 'zod';

const SeasonSessionSchema = z.object({
  booking_id: z.string().uuid(),
  date: z.string(),
  start_time: z.string(),
  end_time: z.string(),
  is_past: z.boolean(),
  is_full: z.boolean(),
  /** Covered by a purchase from the quoted start session. */
  is_included: z.boolean(),
});

export const ClassSeasonQuoteSchema = z.object({
  class_schedule_id: z.string().uuid(),
  starts_on: z.string(),
  ends_on: z.string(),
  /** The customer may start at any session (priced by sessions covered). */
  season_prorate: z.boolean(),
  prorated: z.boolean(),
  total_sessions: z.number().int(),
  /** Sessions not started yet, whatever the start. */
  upcoming_sessions: z.number().int(),
  /** Sessions a purchase from the quoted start covers. */
  remaining_sessions: z.number().int(),
  start_allowed: z.boolean(),
  /** First covered session, null when none. */
  start_booking_id: z.string().uuid().nullable(),
  has_full_session: z.boolean(),
  full_price: z.coerce.number(),
  price: z.coerce.number(),
  sessions: z.array(SeasonSessionSchema),
});

export type ClassSeasonQuote = z.infer<typeof ClassSeasonQuoteSchema>;
export type SeasonSession = z.infer<typeof SeasonSessionSchema>;

/** Why a season can't be bought right now, or null when it can. */
export function getSeasonUnavailableReason(
  quote: ClassSeasonQuote,
): 'ended' | 'start' | 'full' | 'no_sessions' | null {
  if (quote.total_sessions === 0) return 'no_sessions';
  if (quote.upcoming_sessions === 0) return 'ended';
  if (!quote.start_allowed) return 'start';
  if (quote.remaining_sessions === 0) return 'ended';
  if (quote.has_full_session) return 'full';
  return null;
}

const SEASON_ERROR_CODES = [
  'SEASON_NOT_FOUND',
  'SEASON_INVALID_RECIPIENT',
  'SEASON_ALREADY_PURCHASED',
  'SEASON_ALREADY_ENROLLED',
  'SEASON_ENDED',
  'SEASON_START_UNAVAILABLE',
  'SEASON_SESSION_FULL',
  'SEASON_PRICE_CHANGED',
  'SEASON_PAYMENT_MISMATCH',
] as const;

export type SeasonErrorCode = (typeof SEASON_ERROR_CODES)[number];

/** Extracts the stable SEASON_* code a season RPC raised, if any. */
export function getSeasonErrorCode(message: string): SeasonErrorCode | null {
  return SEASON_ERROR_CODES.find((code) => message.includes(code)) ?? null;
}

/** bookings.json keys for each season error. */
export const SEASON_ERROR_I18N_KEYS: Record<SeasonErrorCode, string> = {
  SEASON_NOT_FOUND: 'bookings:season.errors.notFound',
  SEASON_INVALID_RECIPIENT: 'bookings:season.errors.notFound',
  SEASON_ALREADY_PURCHASED: 'bookings:season.errors.alreadyPurchased',
  SEASON_ALREADY_ENROLLED: 'bookings:season.errors.alreadyEnrolled',
  SEASON_ENDED: 'bookings:season.errors.ended',
  SEASON_START_UNAVAILABLE: 'bookings:season.errors.startUnavailable',
  SEASON_SESSION_FULL: 'bookings:season.errors.full',
  SEASON_PRICE_CHANGED: 'bookings:season.errors.priceChanged',
  SEASON_PAYMENT_MISMATCH: 'bookings:season.errors.priceChanged',
};
